import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchNewsByCreatedAt } from '../services/api';
import { useAuth } from './AuthContext';

export type NewsItem = Record<string, unknown>;

type NewsUpdatesContextValue = {
  newsItems: NewsItem[];
  count: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const NewsUpdatesContext = createContext<NewsUpdatesContextValue | undefined>(undefined);

export function NewsUpdatesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user?.token) {
      setNewsItems([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetchNewsByCreatedAt(user.token);
      const news = Array.isArray(res.news) ? res.news : [];
      setNewsItems(news);
    } catch (e) {
      setNewsItems([]);
      setError(e instanceof Error ? e.message : 'Failed to load updates');
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <NewsUpdatesContext.Provider
      value={{
        newsItems,
        count: newsItems.length,
        loading,
        error,
        refresh,
      }}
    >
      {children}
    </NewsUpdatesContext.Provider>
  );
}

export function useNewsUpdates(): NewsUpdatesContextValue {
  const ctx = useContext(NewsUpdatesContext);
  if (!ctx) {
    throw new Error('useNewsUpdates must be used within NewsUpdatesProvider');
  }
  return ctx;
}
